import { useState } from "react";
import { Visit, CreateVisitInput } from "../types/visit";
import { useUser } from "../../../features/settings/hooks/useUser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Calendar } from "../../../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { format } from "date-fns";
import { Checkbox } from "../../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "../../../lib/utils";

interface ScheduleFollowUpDialogProps {
  open: boolean;
  onClose: () => void;
  onSchedule: (data: CreateVisitInput) => void;
  currentVisit: Visit;
}

export function ScheduleFollowUpDialog({ 
  open, 
  onClose, 
  onSchedule,
  currentVisit 
}: ScheduleFollowUpDialogProps) {
  const { data: physician } = useUser(currentVisit.doctorId);
  const [followUps, setFollowUps] = useState([{
    id: crypto.randomUUID(),
    date: undefined as Date | undefined,
    time: "9:00",
    duration: "30",
    notes: "",
    isUrgent: false
  }]);

  const handleSchedule = () => {
    // Validate all follow-ups have dates
    if (followUps.some(f => !f.date)) return;

    // Schedule all follow-ups
    followUps.forEach(followUp => {
      const [hours, minutes] = followUp.time.split(":").map(Number);
      const startTime = new Date(followUp.date!);
      startTime.setHours(hours, minutes);

      onSchedule({
        patientId: currentVisit.patientId,
        clinicId: currentVisit.clinicId,
        doctorId: currentVisit.doctorId,
        type: currentVisit.type,
        startTime: startTime.toISOString(),
        duration: parseInt(followUp.duration),
        reason: "Follow-up",
        initialNotes: `Follow-up visit for visit on ${format(new Date(currentVisit.startTime), "MMMM d, yyyy")}\n\nOriginal Visit Details:\nReason: ${currentVisit.reason}\nDiagnosis: ${currentVisit.diagnosis || "N/A"}\n\nFollow-up Notes:\n${followUp.notes}`,
        originalVisitId: currentVisit.id,
        isUrgent: followUp.isUrgent
      });
    });
  };

  const timeSlots = Array.from({ length: 24 }, (_, hour) => {
    const hourStr = hour.toString().padStart(2, "0");
    return [`${hourStr}:00`, `${hourStr}:30`];
  }).flat();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule Follow-up Visit</DialogTitle>
          {physician && (
            <p className="text-sm text-muted-foreground mt-1">
              Physician: {physician.title} {physician.firstName} {physician.lastName}
              {physician.specialization && (
                <span className="block text-xs">{physician.specialization}</span>
              )}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-6 py-4">
          {followUps.map((followUp, index) => (
            <div key={followUp.id} className="space-y-6 border rounded-lg p-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Follow-up Visit {index + 1}</h3>
                {followUps.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => setFollowUps(followUps.filter(f => f.id !== followUp.id))}
                  >
                    Remove
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !followUp.date && "text-muted-foreground"
                      )}
                    >
                      {followUp.date ? (
                        format(followUp.date, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={followUp.date}
                      onSelect={(date) => {
                        setFollowUps(followUps.map(f => 
                          f.id === followUp.id ? { ...f, date } : f
                        ));
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const compareDate = new Date(date);
                        compareDate.setHours(0, 0, 0, 0);
                        return compareDate < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Select 
                    value={followUp.time} 
                    onValueChange={(value) => {
                      setFollowUps(followUps.map(f => 
                        f.id === followUp.id ? { ...f, time: value } : f
                      ));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select 
                    value={followUp.duration} 
                    onValueChange={(value) => {
                      setFollowUps(followUps.map(f => 
                        f.id === followUp.id ? { ...f, duration: value } : f
                      ));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Follow-up Notes</Label>
                <Textarea
                  value={followUp.notes}
                  onChange={(e) => {
                    setFollowUps(followUps.map(f => 
                      f.id === followUp.id ? { ...f, notes: e.target.value } : f
                    ));
                  }}
                  placeholder="Add any specific notes for the follow-up visit"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`urgent-${followUp.id}`}
                  checked={followUp.isUrgent}
                  onCheckedChange={(checked) => {
                    setFollowUps(followUps.map(f => 
                      f.id === followUp.id ? { ...f, isUrgent: checked as boolean } : f
                    ));
                  }}
                />
                <Label htmlFor={`urgent-${followUp.id}`}>Mark as Urgent</Label>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setFollowUps([...followUps, {
              id: crypto.randomUUID(),
              date: undefined,
              time: "9:00",
              duration: "30",
              notes: "",
              isUrgent: false
            }])}
          >
            Add Another Follow-up
          </Button>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSchedule} 
            disabled={followUps.some(f => !f.date)}
            className="w-full"
          >
            Schedule Visit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
